import React from 'react'
import { FormattedMessage } from 'react-intl'
import FormatCurrency from 'components/Common/FormatCurrency/FormatCurrency'
import LineCrownIcon from 'LuxKit/Icons/line/LineCrownIcon'
import LineTaxiIcon from 'LuxKit/Icons/line/LineTaxiIcon'
import LineChairWithAirplaneIcon from 'LuxKit/Icons/line/LineChairWithAirplaneIcon'
import LineShieldCheckIcon from 'LuxKit/Icons/line/LineShieldCheckIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import LineCompassIcon from 'LuxKit/Icons/line/LineCompassIcon'
import LineCameraIcon from 'LuxKit/Icons/line/LineCameraIcon'

export const TIER_PROGRESSION_BENEFIT_ORDER: Array<App.LoyaltyBenefitType> = [
  'hotel_upgrade',
  'lounge_pass',
  'airport_transfer',
  'discounted_insurance',
  'tour_exp_credit',
  'travelshoot_credit',
  'phone_support',
]

export const BENEFIT_ICONS: Record<App.LoyaltyBenefitType, React.ReactNode> = {
  hotel_upgrade: <LineCrownIcon size="XS" />,
  airport_transfer: <LineTaxiIcon size="XS" />,
  lounge_pass: <LineChairWithAirplaneIcon size="XS" />,
  discounted_insurance: <LineShieldCheckIcon size="XS" />,
  phone_support: <LinePhoneIcon size="XS" />,
  tour_exp_credit: <LineCompassIcon size="XS" />,
  travelshoot_credit: <LineCameraIcon size="XS" />,
  early_access: null,
  esim_package: null,
  hidden_access: null,
}

interface Props {
  benefit: App.LoyaltyRegionTierBenefit
}

function TierProgressionBenefitTitle({ benefit }: Props): React.ReactElement {
  const { code, limit, unlimited, value } = benefit
  const count = limit ?? 1

  switch (code) {
    case 'hotel_upgrade':
      if (unlimited) {
        return (
          <FormattedMessage
            id="ahqUG24FxP"
            defaultMessage="Unlimited free room or package upgrades"
            description="Benefit title for unlimited room upgrades"
          />
        )
      }
      return (
        <FormattedMessage
          id="rD+BVUnrq3"
          defaultMessage="{count, plural, one {1x Free room or package upgrade} other {#x Free room or package upgrades}}"
          description="Benefit title for limited room upgrades"
          values={{ count }}
        />
      )
    case 'airport_transfer':
      if (unlimited) {
        return (
          <FormattedMessage
            id="zuce/bwMBx"
            defaultMessage="Unlimited free airport chauffeur service"
            description="Benefit title for unlimited airport transfers"
          />
        )
      }
      return (
        <FormattedMessage
          id="ApEZjFHsix"
          defaultMessage="{count, plural, one {Free airport chauffeur service} other {#x Free airport chauffeur service}}"
          description="Benefit title for limited airport transfers"
          values={{ count }}
        />
      )
    case 'lounge_pass':
      if (unlimited) {
        return (
          <FormattedMessage
            id="ZMeVFZYqEz"
            defaultMessage="Unlimited free airport lounge passes"
            description="Benefit title for unlimited lounge passes"
          />
        )
      }
      return (
        <FormattedMessage
          id="lRQGm4fR1U"
          defaultMessage="{count, plural, one {Free airport lounge pass} other {#x Free airport lounge passes}}"
          description="Benefit title for limited lounge passes"
          values={{ count }}
        />
      )
    case 'discounted_insurance':
      return (
        <FormattedMessage
          id="t7uKMLnAPV"
          defaultMessage="{percent}% Travel protection discount"
          description="Benefit title for travel insurance discount"
          values={{ percent: value?.amount ?? 0 }}
        />
      )
    case 'phone_support':
      return (
        <FormattedMessage
          id="p8+JiTdveX"
          defaultMessage="Priority phone support 24/7"
          description="Benefit title for priority phone support"
        />
      )
    case 'tour_exp_credit':
      return (
        <FormattedMessage
          id="Hb5Gd80EW9"
          defaultMessage="{count, plural, one {<currency></currency> Tour optional extra credit} other {#x <currency></currency> Tour optional extra credits}}"
          description="Benefit title for tour experience credits"
          values={{
            count,
            currency: () => <FormatCurrency value={value?.amount ?? 0} />,
          }}
        />
      )
    case 'travelshoot_credit':
      return (
        <FormattedMessage
          id="vGWf1aCv3u"
          defaultMessage="<currency></currency> TravelShoot voucher"
          description="Benefit title for TravelShoot voucher"
          values={{
            currency: () => <FormatCurrency value={value?.amount ?? 0} />,
          }}
        />
      )
    default:
      return <>{code}</>
  }
}

export default TierProgressionBenefitTitle
